import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import type { DialogFormComponent as DialogFormComponentType } from '@modules/forms/components/dialog-form/dialog-form.component';
import { FormOutputs } from '@modules/forms/models/form-outputs';
import { ModuleResolverInputs } from '@modules/forms/models/module-resolver-inputs';
import { buildSadmEntityDraft } from '@modules/sadm/utils/build-sadm-entity-draft';
import { DEFAULT_SADM_ENTITY_NAME_KEY } from '@modules/sadm/utils/default-sadm-entity-name-key';
import { Store } from '@ngrx/store';
import { generateEntityName, SadmClient } from '@pixacare/dtos';
import { Form, FormAnswer } from '@pixacare/forms';
import { isNullOrEmpty } from '@pixacare/shared';
import { DialogService } from '@services/dialog.service';
import { SadmEntityDraft } from '@shared/models/draft/sadm-entity-draft';
import { sadmActions } from '@shared/store/sadm/sadm.actions';
import { filter, first, from, map, Observable, switchMap } from 'rxjs';
import { selectSadmEntity } from 'src/app/shared/store/sadm/sadm.selectors';
import { SadmEntityDisplay } from '../models/sadm-entity-display';
import { SadmTypeLabelService } from './sadm-type-label.service';

@Injectable()
export class SadmEntityService {
  private readonly dialogService = inject(DialogService);
  private readonly translocoService = inject(TranslocoService);
  private readonly sadmTypeLabelService = inject(SadmTypeLabelService);
  private readonly store = inject(Store);

  static getNewSadmEntityName({
    sadmClient,
    currentName,
    currentAnswer,
    newAnswer,
  }: {
    sadmClient: SadmClient;
    currentName: string;
    currentAnswer: FormAnswer;
    newAnswer: FormAnswer;
  }): string {
    const theoricalName = generateEntityName(sadmClient.entityCreationForm, currentAnswer.answers, sadmClient.sadmType);
    const newName = generateEntityName(sadmClient.entityCreationForm, newAnswer.answers, sadmClient.sadmType);

    const isCurrentAnswersEmpty = isNullOrEmpty(Object.keys(currentAnswer.answers ?? {}));
    if (isCurrentAnswersEmpty) {
      return newName;
    }

    return currentName === theoricalName ? newName : currentName;
  }

  openEntityCreation(
    sadmClient: SadmClient,
    patientId: number,
    clientCode: string,
    formInputs: ModuleResolverInputs,
  ): Observable<SadmEntityDraft> {
    return from(import('@modules/forms/components/dialog-form/dialog-form.component')).pipe(
      switchMap(({ DialogFormComponent }) =>
        this.dialogService
          .openComponentWithCloseConfirmation<DialogFormComponentType, FormOutputs>(DialogFormComponent, {
            label: this.translocoService.translate('Créer une {{sadmTypeLabel}}', {
              sadmTypeLabel: this.sadmTypeLabelService.getLabel(sadmClient.sadmType),
            }),
            data: {
              form: sadmClient.entityCreationForm,
              formInputs,
            },
            dismissible: false,
            size: 'm',
          })
          .pipe(
            map((result) =>
              result
                ? buildSadmEntityDraft(sadmClient, {
                    patientId,
                    clientCode,
                    answer: result,
                    fallbackName: this.translocoService.translate(DEFAULT_SADM_ENTITY_NAME_KEY),
                  })
                : null,
            ),
          ),
      ),
    );
  }

  editEntityCreationForm(sadmEntity: SadmEntityDisplay): void {
    this.store
      .select(selectSadmEntity(sadmEntity.id))
      .pipe(
        first(),
        switchMap(({ sadmClient }) =>
          this.openEntityCreationForm(sadmClient.entityCreationForm, sadmEntity.entityCreationFormAnswer),
        ),
        filter(Boolean),
        map(({ pictures, ...entityCreationFormAnswer }) => entityCreationFormAnswer),
      )
      .subscribe((entityCreationFormAnswer) =>
        this.store.dispatch(
          sadmActions.updateSadmEntity({
            sadmEntityId: sadmEntity.id,
            updateSadmEntityRequest: {
              name: SadmEntityService.getNewSadmEntityName({
                sadmClient: sadmEntity.sadmClient,
                currentName: sadmEntity.name,
                currentAnswer: sadmEntity.entityCreationFormAnswer,
                newAnswer: entityCreationFormAnswer,
              }),
              entityCreationFormAnswer,
            },
          }),
        ),
      );
  }

  private openEntityCreationForm(form: Form, formAnswer: FormAnswer): Observable<FormOutputs> {
    return from(import('@modules/forms/components/dialog-form/dialog-form.component')).pipe(
      switchMap(({ DialogFormComponent }) =>
        this.dialogService.openComponent<DialogFormComponentType, FormOutputs>(DialogFormComponent, {
          label: this.translocoService.translate('Documenter une lésion'),
          data: { form, formAnswer, formInputs: {} },
          dismissible: false,
          size: 'm',
        }),
      ),
    );
  }
}
